import { useState, useCallback } from 'react';
import { API } from '@/utils/API';
import { SubmissionTemplateData } from '@/features/submission_forms/types';

interface UseSubmissionTemplateFormResult {
  fetchTemplateForm: () => Promise<void>;
  templateFormData: SubmissionTemplateData | null;
  isLoading: boolean;
  error: string | null;
}

const fetchTemplateFormAction = async (templateFormId: string | undefined) => {
  if (!templateFormId) {
    throw new Error('No form ID provided');
  }

  const response = await API.authenticated.fetch(
    `/submission_templates/${templateFormId}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch form');
  }

  return response.json();
};

export const useSubmissionTemplateForm = (
  templateFormId: string | undefined
): UseSubmissionTemplateFormResult => {
  const [templateFormData, setTemplateFormData] =
    useState<SubmissionTemplateData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplateForm = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchTemplateFormAction(templateFormId);
      setTemplateFormData(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch form';
      setError(errorMessage);
      setTemplateFormData(null);
    } finally {
      setIsLoading(false);
    }
  }, [templateFormId]);

  return {
    fetchTemplateForm,
    templateFormData,
    isLoading,
    error,
  };
};
